import { createReducer, on } from "@ngrx/store";

import { OptionSelection } from "./../../../shared/types/processing-options.types";
import { DocumentActions } from ".";
import {
  DocumentResultResponse,
  DocumentUpload,
  DocumentUploadResponse,
  NormalizedTranscription,
  PortalUploadStatus,
  ProcessAnalysisOptions,
  TableData,
  UPLOAD_WINDOW_EXPIRED_MESSAGE,
  UploadSession,
  UploadStep,
} from "./document.model";

export interface DocumentState {
  uploadResult: DocumentUploadResponse | null;
  documentResult: DocumentResultResponse | null;
  currentDocument: DocumentUpload | null;
  supportedFileTypes: string[];
  maxFileSizeBytes: number | null;
  convertedPdf: Blob | null;
  status: string;
  documentError: string;
  modelType: string | null;
  selectedOption: OptionSelection | null;
  tablesData: TableData[] | null;
  uploadSession: UploadSession | null;
  uploadStep: UploadStep;
  portalUploadStatus: PortalUploadStatus;
  portalUploadProgress: number;
  // Resolved model/extraction settings captured at step 1 so the auto-process
  // effect can dispatch processUpload without re-reading component state.
  resolvedOptions: ProcessAnalysisOptions | null;
  transcription: NormalizedTranscription | null;
  audioBlob: Blob | null;
}

export const initialState: DocumentState = {
  uploadResult: null,
  documentResult: null,
  currentDocument: null,
  supportedFileTypes: [],
  maxFileSizeBytes: null,
  convertedPdf: null,
  status: "",
  documentError: "",
  modelType: null,
  selectedOption: null,
  tablesData: null,
  uploadSession: null,
  uploadStep: "idle",
  portalUploadStatus: "idle",
  portalUploadProgress: 0,
  resolvedOptions: null,
  transcription: null,
  audioBlob: null,
};

export const documentReducer = createReducer(
  initialState,

  on(DocumentActions.analyzeDocument, (state, { document }) => ({
    ...state,
    currentDocument: document,
    modelType: (document.file as any).modelType || null,
    // Enter the loading panel immediately on submit so the template can branch
    // to the v1 progress view before the POST returns. Reset portalUploadStatus
    // so a previous error state doesn't bleed into a retry attempt.
    uploadStep: "processing" as const,
    documentError: "",
    status: "",
    documentResult: null,
    portalUploadStatus: "idle" as const,
  })),

  on(DocumentActions.analyzeDocumentSuccess, (state, { result }) => ({
    ...state,
    uploadResult: result,
    status: "success",
    // "uploaded" signals that the file was accepted by the server; the
    // upload-session component uses this to flip isProcessing and switch
    // the loader message from "Uploading…" to "Analyzing Document…".
    portalUploadStatus: "uploaded" as const,
  })),

  on(
    DocumentActions.initializeUpload,
    (state, { document, resolvedOptions }): DocumentState => ({
      ...state,
      currentDocument: document,
      modelType: document.file.modelType || null,
      uploadSession: null,
      uploadStep: "initializing",
      portalUploadStatus: "idle",
      portalUploadProgress: 0,
      documentResult: null,
      documentError: "",
      status: "",
      resolvedOptions,
    })
  ),

  on(
    DocumentActions.initializeUploadSuccess,
    (state, { session }): DocumentState => ({
      ...state,
      uploadSession: session,
      uploadStep: "awaitingUpload",
    })
  ),

  on(
    DocumentActions.initializeUploadFailure,
    (state, { error }): DocumentState => ({
      ...state,
      documentError: error,
      status: "error",
      uploadStep: "idle",
      uploadSession: null,
      currentDocument: null,
    })
  ),

  on(DocumentActions.uploadToBlob, (state): DocumentState => ({
    ...state,
    portalUploadStatus: "uploading",
    portalUploadProgress: 0,
    documentError: "",
  })),

  on(DocumentActions.uploadToBlobProgress, (state, { percent }) => ({
    ...state,
    portalUploadProgress: percent,
  })),

  on(DocumentActions.uploadToBlobSuccess, (state): DocumentState => ({
    ...state,
    portalUploadStatus: "uploaded",
    portalUploadProgress: 100,
  })),

  // An upload failure stays retryable: the panel keeps its actions enabled and
  // the message is shown below. (Expiry can't be detected on the PUT — see the
  // action — so there's no "expired" lock here, unlike the process path.)
  on(
    DocumentActions.uploadToBlobFailure,
    (state, { error }): DocumentState => ({
      ...state,
      portalUploadStatus: "error",
      documentError: error,
    })
  ),

  on(DocumentActions.processUpload, (state, { options }): DocumentState => ({
    ...state,
    uploadStep: "processing",
    documentError: "",
    status: "",
    // Reflect the step-2 line-numbers choice in the snapshot the results
    // view reads (the file itself is frozen, so options carry the value).
    currentDocument: state.currentDocument
      ? {
          ...state.currentDocument,
          includeLineNumbers: options.includeLineNumbers,
        }
      : state.currentDocument,
  })),

  // Return to the upload-session panel on any process failure. An expired SAS
  // (410) marks the panel "expired" so its actions disable in place; other
  // failures stay retryable so the user can finish uploading and process again.
  on(
    DocumentActions.processUploadFailure,
    (state, { error, expired }): DocumentState => ({
      ...state,
      uploadStep: "awaitingUpload",
      documentError: error,
      status: "error",
      portalUploadStatus: expired ? "expired" : state.portalUploadStatus,
    })
  ),

  on(DocumentActions.analyzeDocumentFailure, (state, { error }) => ({
    ...state,
    documentError: error,
    status: "error",
    // Only enter the retryable panel when an upload was already in flight.
    // If uploadStep is "idle" the failure came from client-side file validation
    // (e.g. wrong type, oversized) — no upload started, so keep the file
    // picker visible rather than replacing it with a blank upload-session panel.
    uploadStep:
      state.uploadStep === "idle" ? "idle" : ("awaitingUpload" as const),
    portalUploadStatus:
      state.uploadStep === "idle"
        ? state.portalUploadStatus
        : ("error" as const),
  })),

  on(DocumentActions.documentResultSuccess, (state, { result }) => {
    switch (result.status) {
      case "Completed":
      case "Pending":
      // v2: SAS issued, file not yet processed — treated as in-progress so
      // polling continues.
      case "AwaitingUpload":
        return {
          ...state,
          documentResult: result,
        };
      case "Failed":
        return {
          ...state,
          documentResult: result,
          status: "error",
          documentError: result.error
            ? result.error.description
            : result.failureReason,
          // Drop back to awaitingUpload so isProcessing becomes false,
          // hasProcessError becomes true, and the failure card renders.
          uploadStep: "awaitingUpload" as const,
        };
      // v2: the upload SAS expired before the file finished uploading. The
      // session can't be reused, so reset to idle (not awaitingUpload) and
      // clear it — otherwise the step rail keeps "Process" highlighted while
      // the error tells the user to start over.
      case "UploadExpired":
        return {
          ...state,
          documentResult: result,
          status: "error",
          documentError: UPLOAD_WINDOW_EXPIRED_MESSAGE,
          uploadStep: "idle",
          uploadSession: null,
        };
      default:
        return state;
    }
  }),

  on(DocumentActions.documentResultsBlobSuccess, (state, { results }) => ({
    ...state,
    documentResult: state.documentResult
      ? { ...state.documentResult, results }
      : state.documentResult,
  })),

  on(DocumentActions.documentResultsBlobFailure, (state, { error }) => ({
    ...state,
    documentError: error,
    status: "error",
  })),

  // A result-fetch failure while processing must drop back to awaitingUpload so
  // isProcessing becomes false, hasProcessError becomes true, and the panel
  // surfaces the error with a Retry button rather than staying silently stuck.
  on(DocumentActions.documentResultFailure, (state, { error }) => ({
    ...state,
    documentError: error,
    uploadStep: "awaitingUpload" as const,
  })),

  on(DocumentActions.clearDocumentError, (state): DocumentState => ({
    ...state,
    documentError: "",
  })),

  on(DocumentActions.resetState, (state): DocumentState => ({
    ...state,
    documentError: "",
    status: "",
    uploadResult: null,
    documentResult: null,
    currentDocument: null,
    modelType: null,
    tablesData: null,
    uploadSession: null,
    uploadStep: "idle",
    portalUploadStatus: "idle",
    portalUploadProgress: 0,
    resolvedOptions: null,
    // Clear the stale blob so the next non-PDF document doesn't reuse the
    // previous file's converted PDF as its preview while the new one loads.
    convertedPdf: null,
    transcription: null,
    audioBlob: null,
  })),

  on(DocumentActions.documentConvertedPdfSuccess, (state, { pdfFile }) => ({
    ...state,
    convertedPdf: pdfFile,
  })),

  on(
    DocumentActions.setSupportedFileTypes,
    (state, { supportedFileTypes }) => ({
      ...state,
      supportedFileTypes: supportedFileTypes,
    })
  ),
  on(DocumentActions.updateProcessingOption, (state, { option }) => ({
    ...state,
    selectedOption: option,
  })),

  on(DocumentActions.documentTablesDataSuccess, (state, { tablesData }) => ({
    ...state,
    tablesData: tablesData,
  })),

  on(DocumentActions.documentTablesDataFailure, (state, { error }) => ({
    ...state,
    documentError: error,
  })),

  on(DocumentActions.setFileSizeLimit, (state, { maxFileSizeBytes }) => ({
    ...state,
    maxFileSizeBytes: maxFileSizeBytes,
  })),

  on(
    DocumentActions.documentTranscriptionSuccess,
    (state, { transcription }) => ({
      ...state,
      transcription,
    })
  ),

  on(DocumentActions.documentTranscriptionFailure, (state, { error }) => ({
    ...state,
    documentError: error,
  })),

  on(DocumentActions.documentAudioSuccess, (state, { audioBlob }) => ({
    ...state,
    audioBlob,
  })),

  on(DocumentActions.documentAudioFailure, (state, { error }) => ({
    ...state,
    documentError: error,
  }))
);
